import { LoyaltyTagAPIPromiseClient } from "gen/witbooking/loyalty/tag/v1/loyalty_tag_api_grpc_web_pb";
import { useCallback, useMemo, useState } from "react";
import { Button, Card, Container } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { FormattedMessage } from "react-intl";
import { useHistory } from "react-router";
import styled from "styled-components";
import ContentWrapper from "../../../components/content-wrapper";
import { useLoyaltyBrand } from "../../../components/loyalty-brand-provider";
import { useProperty } from "../../../components/property-provider";
import { SectionHeading } from "../../../components/section-heading";
import { useGrpcClient } from "../../../lib/grpc";
import { createExcelFileDownloadFromRawData } from "../../../lib/supportFunctions";
import { StyledSpinner } from "../../ariSnapshot/styles";
import { LoyaltyTagMultiUpdateResult } from "../domain/LoyaltyTagMultiUpdateResult";
import { LoyaltyTagGateway } from "../infrastructure/gateways/LoyaltyTagGateway";
import { loyaltyTagMultiUpdateMessages } from "./LoyaltyTagMultiUpdate.messages";
import { LoyaltyTagMultiUpdateResults } from "./LoyaltyTagMultiUpdateResults";

const DropzoneContainer = styled.div`
  height: 100px;
  border: 2px dashed #dae6e8;
  background-color: ${({ theme }) => theme.greyBackground};
  font-size: 21px;

  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  outline: none;
`;

export function LoyaltyTagMultiUpdate() {
  const brandId = useLoyaltyBrand()?.externalSystemId || "";
  const property = useProperty();
  const history = useHistory();
  const grpcClient = useGrpcClient(LoyaltyTagAPIPromiseClient);

  const [file, setFile] = useState<File>();
  const [createAllResult, setCreateAllResult] = useState<LoyaltyTagMultiUpdateResult>();
  const [loading, setLoading] = useState(false);
  const loyaltyTagGateway = useMemo(() => new LoyaltyTagGateway(grpcClient), [grpcClient]);

  const handleOnDrop = useCallback(<T extends File>(acceptedFiles: T[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) {
      return;
    }
    setFile(acceptedFiles[0]);
    setCreateAllResult(undefined);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({ onDrop: handleOnDrop });

  const handleOnImport = useCallback(async () => {
    if (!file) return;

    try {
      setLoading(true);
      const saveAllResult = await loyaltyTagGateway.saveAll(brandId, file);
      setCreateAllResult(saveAllResult);
    } catch (e) {
      console.error("Error importing file", e);
    } finally {
      setLoading(false);
    }
  }, [brandId, file, loyaltyTagGateway]);

  const handleOnDownloadTemplate = useCallback(async () => {
    const data = await loyaltyTagGateway.downloadSaveAllTemplate(brandId);
    createExcelFileDownloadFromRawData(data, "loyalty-preferences.xlsx");
  }, [brandId, loyaltyTagGateway]);

  const handleGoBack = useCallback(() => {
    history.push(`/${property.ticker}/loyalty-preferences/preferences`);
  }, [history, property.ticker]);

  return (
    <ContentWrapper>
      <SectionHeading>
        <FormattedMessage {...loyaltyTagMultiUpdateMessages.title} />
      </SectionHeading>
      <Container className="flex flex-col gap-4 items-center p-4" fluid={true}>
        <Card className="flex flex-col gap-4 p-4 w-full">
          <span className="text-[16px]">
            <FormattedMessage {...loyaltyTagMultiUpdateMessages.instructions} />
          </span>
          <span className="text-sm">
            <FormattedMessage {...loyaltyTagMultiUpdateMessages.instructionsDetail} />
          </span>
          <DropzoneContainer {...getRootProps()}>
            <input {...getInputProps()} />
            {file ? (
              <span>{file.name}</span>
            ) : (
              <FormattedMessage {...loyaltyTagMultiUpdateMessages.dropFiles} />
            )}
          </DropzoneContainer>

          {createAllResult && <LoyaltyTagMultiUpdateResults result={createAllResult} />}

          <div className="flex gap-4">
            <Button type="primary" onClick={handleOnImport} disabled={loading}>
              <div className="flex gap-2 items-center justify-center">
                {loading && <StyledSpinner className="fa fa-spinner fa-spin" />}
                <FormattedMessage {...loyaltyTagMultiUpdateMessages.btnImportFile} />
              </div>
            </Button>
            <Button type="primary" onClick={handleOnDownloadTemplate} disabled={loading}>
              <FormattedMessage {...loyaltyTagMultiUpdateMessages.btnDownloadFile} />
            </Button>
          </div>
        </Card>

        <Button type="primary" onClick={handleGoBack} disabled={loading}>
          <FormattedMessage {...loyaltyTagMultiUpdateMessages.back} />
        </Button>
      </Container>
    </ContentWrapper>
  );
}
